import { Location } from '@angular/common';
import { Injectable, NgZone, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GuardsCheckEnd, NavigationError, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { trackEvent } from '../analytics';
import { AnalyticsEventParams } from '../analytics/analytics.types';
import { NotificationService } from './notification.service';
import { PendingWritesService } from './pending-writes.service';

/**
 * Set just before a reload the user asked for, read and cleared on the next
 * load, which then says the update was applied.
 */
export const UPDATED_NOTICE_KEY = 'dmt_update_applied';

/**
 * Automatic reloads closer together than this are treated as a loop (for
 * example the server keeps handing out a stale page) and stop; the user gets the
 * bar instead. One key, overwritten each time, so it never grows.
 */
export const AUTO_RELOAD_COOLDOWN_MS = 60_000;
export const AUTO_RELOAD_KEY = 'dmt_auto_reload_at';

/**
 * Why this service last reloaded the page, read and cleared on the next load so
 * the console says whether a reload came from here or from somewhere else.
 */
export const LAST_RELOAD_KEY = 'dmt_update_reload';

/**
 * A reload the user asked for waits this long for saves in flight before
 * asking whether to interrupt them (an upload can take minutes).
 */
const WRITE_WAIT_MS = 3_000;

/**
 * How often an open tab asks for a new version. The service worker only looks
 * by itself when a page loads, which a tab kept open all day never does.
 */
export const UPDATE_CHECK_INTERVAL_MS = 10 * 60_000;

const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

export interface ReloadBlocker {
  /** True while an automatic reload would cost the user something. */
  blocksAutoReload(): boolean;
  /** True while a reload the user asks for should be confirmed first. */
  hasUnsavedChanges?(): boolean;
}

export type UpdatePrompt = 'none' | 'bar' | 'updated';
type UpdateAction = AnalyticsEventParams['app_update']['update_action'];

/** True when the focused element is somewhere the user can type. */
export function isEditingText(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    return !NON_TEXT_INPUTS.has(el.type) && !el.readOnly && !el.disabled;
  }
  return (el as HTMLElement).isContentEditable === true;
}

/**
 * True for the error a lazy-loaded page throws when its files are gone, which
 * happens to a tab still running a version older than the one deployed.
 */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  if (!e) return false;
  if (e.name === 'ChunkLoadError') return true;
  return /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    e.message ?? '',
  );
}

function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

/**
 * Moves the app onto a newly deployed version without throwing away anyone's
 * work.
 *
 * - On the sign-in page it reloads silently once nothing is in progress, and
 *   leaving that page after signing in is a full page load onto the new
 *   version.
 * - Everywhere else it never reloads by itself: a bar offers "Update now", and
 *   closing it leaves the update for the next time Dormotech is opened. After
 *   "Update now" the reloaded page says the update was applied.
 * - A page whose files are missing shows the red bar instead of reloading.
 */
@Injectable({ providedIn: 'root' })
export class SwUpdateService {
  readonly prompt = signal<UpdatePrompt>('none');
  /** The running version cannot keep working; the bar asks for a reload. */
  readonly mustReload = signal(false);

  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private beforeInstallHandler: ((event: Event) => void) | null = null;
  private subscriptions: Subscription = new Subscription();
  private scheduledCheckTimeout: any;
  private periodicCheck: ReturnType<typeof setInterval> | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;

  private blockers: ReloadBlocker[] = [];
  private updateReady = false;
  // Ready on the sign-in page, waiting for a safe moment to reload silently.
  private deciding = false;

  constructor(
    private swUpdate: SwUpdate,
    private router: Router,
    private location: Location,
    private zone: NgZone,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private pendingWrites: PendingWritesService,
  ) {}

  public initialize() {
    this.reportLastReload();
    this.showUpdatedNoticeIfApplied();
    // Missing page files can strand a tab even without a service worker.
    this.watchNavigation();

    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker is not enabled');
      return;
    }

    // Block PWA install prompt
    this.beforeInstallHandler = (event: Event) => {
      event.preventDefault();
      console.log('PWA install prompt blocked.');
    };
    window.addEventListener('beforeinstallprompt', this.beforeInstallHandler);


    this.subscriptions.add(
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe((event) => {
          console.log('New version ready:', event.latestVersion?.hash);
          this.onUpdateReady();
        }),
    );

    this.subscriptions.add(
      this.swUpdate.unrecoverable.subscribe((event) => {
        console.error('Service Worker in unrecoverable state:', event.reason);
        this.onUnrecoverable();
      }),
    );

    // Initial check after 1 second
    this.scheduledCheckTimeout = setTimeout(() => this.checkForUpdate(), 1000);

    // Check when tab becomes visible
    this.visibilityHandler = () => {
      if (!document.hidden) this.checkForUpdate();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Check on online event
    this.onlineHandler = () => this.checkForUpdate();
    window.addEventListener('online', this.onlineHandler);

    // Outside Angular, so the timer neither runs change detection nor keeps the
    // app from counting as stable. A hidden tab is checked when it is shown.
    this.zone.runOutsideAngular(() => {
      this.periodicCheck = setInterval(() => {
        if (!document.hidden && !this.updateReady) this.checkForUpdate();
      }, UPDATE_CHECK_INTERVAL_MS);
    });
  }

  /**
   * Registers something on screen that a reload would hurt. Call the returned
   * function when it goes away.
   */
  public addReloadBlocker(blocker: ReloadBlocker): () => void {
    this.blockers = [...this.blockers, blocker];
    return () => {
      this.blockers = this.blockers.filter((b) => b !== blocker);
    };
  }

  /**
   * Safe means nothing on the page would be lost or cut off: no text field has
   * focus, no dialog is open (forms such as a new patient or a new test live in
   * dialogs), nothing registered objects, no save is in flight, and there is a
   * network to load the page from.
   */
  public isSafeToReload(): boolean {
    return this.unsafeReason() === null;
  }

  private unsafeReason(): string | null {
    if (!navigator.onLine) return 'offline';
    if (isEditingText(document.activeElement)) return 'typing in a field';
    if (this.dialog.openDialogs.length > 0) return 'a dialog is open';
    if (this.blockers.some((b) => b.blocksAutoReload())) {
      return 'the page has unsaved work or must not reload';
    }
    if (!this.pendingWrites.isIdle()) return 'a save is in progress';
    return null;
  }

  /** The bar's close button: the update waits for the next page load. */
  public later() {
    this.track('later');
    console.info('[update] Bar closed; the new version applies on the next page load.');
    this.zone.run(() => this.prompt.set('none'));
  }

  public closeUpdatedNotice() {
    if (this.prompt() === 'updated') this.prompt.set('none');
  }

  /** "Update now" or "Reload" on the bar. */
  public async updateNow() {
    this.track('update_now');
    this.stopTicker();

    const unsaved =
      this.dialog.openDialogs.length > 0 ||
      this.blockers.some((b) => b.hasUnsavedChanges?.());
    if (unsaved && !(await this.confirmDiscard())) {
      this.showBar();
      return;
    }

    await this.pendingWrites.whenIdle(WRITE_WAIT_MS);
    if (!this.pendingWrites.isIdle() && !(await this.confirmInterrupt())) {
      this.showBar();
      return;
    }
    this.noteReload('update_now');
    if (!this.mustReload()) this.markUpdateApplied();
    void this.reloadPage();
  }

  private onUpdateReady() {
    // Pages rendered for a PDF must never reload or show a prompt mid-render.
    if (this.updateReady || this.isRenderOnlyPage()) return;
    this.updateReady = true;
    this.deciding = true;
    this.decide();
    if (this.deciding) this.startTicker();
  }

  private onUnrecoverable() {
    if (this.isRenderOnlyPage()) return;
    this.updateReady = true;
    this.deciding = false;
    this.mustReload.set(true);
    if (
      this.isOnSignInPage() &&
      this.isSafeToReload() &&
      this.autoReload('unrecoverable')
    ) {
      return;
    }
    this.showBar();
  }

  private tick() {
    if (this.deciding) {
      this.decide();
    } else {
      this.stopTicker();
    }
  }

  // Typing credentials, or signed in and about to leave: wait for the field to
  // be left or for the move off the page, which loads the new version. Left
  // without a full load (offline, a request in flight), the bar asks instead.
  private decide() {
    if (!this.isOnSignInPage()) {
      this.track('shown');
      this.showBar();
      return;
    }
    if (!this.isSafeToReload() || this.isSigningIn()) return;

    this.deciding = false;
    this.stopTicker();
    if (!this.autoReload('silent')) this.showBar();
  }

  private showBar() {
    console.info('[update] Waiting for the user to click Update now; no reload until then.');
    this.deciding = false;
    this.stopTicker();
    this.zone.run(() => this.prompt.set('bar'));
  }

  // Stays until the user closes it.
  private showUpdatedNoticeIfApplied() {
    if (!this.takeUpdateApplied() || this.isRenderOnlyPage()) return;
    console.info('[update] The new version was applied.');
    this.prompt.set('updated');
  }

  private watchNavigation() {
    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof GuardsCheckEnd && event.shouldActivate) {
          if (this.shouldLoadNextPageFresh(event.urlAfterRedirects)) {
            const replace =
              !!this.router.getCurrentNavigation()?.extras?.replaceUrl;
            this.autoReload('on_navigation', event.urlAfterRedirects, replace);
          }
        } else if (
          event instanceof NavigationError &&
          isChunkLoadError(event.error)
        ) {
          this.recoverFromMissingPage(event.url);
        }
      }),
    );
  }

  /**
   * Off the sign-in page the user is asked with the red bar rather than
   * reloaded; lazy pages are fetched before the leave-page guards run, so the
   * unsaved-changes dialog has not been asked yet either. Offline, the files
   * are missing for lack of a network, not because of a new version, and a
   * reload would only lose the page.
   */
  private recoverFromMissingPage(url: string) {
    if (!navigator.onLine) return;
    if (
      this.isOnSignInPage() &&
      this.isSafeToReload() &&
      this.autoReload('chunk_recovery', url)
    ) {
      return;
    }
    this.track('chunk_recovery');
    this.updateReady = true;
    this.deciding = false;
    this.mustReload.set(true);
    this.showBar();
  }

  /**
   * Only when leaving the sign-in page, where the app loads anyway: signing in
   * then lands on the new version instead of meeting the bar. Inside the app a
   * page change stays a page change; the bar does the asking.
   */
  private shouldLoadNextPageFresh(targetUrl: string): boolean {
    if (!this.updateReady || !this.isOnSignInPage()) return false;
    if (pathOf(targetUrl) === pathOf(this.router.url)) return false;
    if (this.router.getCurrentNavigation()?.extras?.skipLocationChange) {
      return false;
    }
    return this.pendingWrites.isIdle() && navigator.onLine;
  }

  /**
   * Reloads the page, or loads `url` in full when given (`replace` keeps the
   * navigation's replaceUrl). Refuses, returning false, when another automatic
   * reload happened too recently.
   */
  private autoReload(
    action: UpdateAction,
    url?: string,
    replace = false,
  ): boolean {
    if (!this.autoReloadAllowed()) {
      console.warn('Automatic reload skipped: one just happened.');
      return false;
    }
    this.markAutoReload();
    this.track(action);
    this.noteReload(action);
    this.stopTicker();
    if (url) {
      this.loadPage(this.location.prepareExternalUrl(url), replace);
    } else {
      void this.reloadPage();
    }
    return true;
  }

  private noteReload(reason: UpdateAction) {
    console.info(`[update] Reloading the page: ${reason}.`);
    try {
      sessionStorage.setItem(
        LAST_RELOAD_KEY,
        `${reason} at ${new Date().toLocaleTimeString()}`,
      );
    } catch {
      // Storage blocked: only the message above is lost after the reload.
    }
  }

  // Tells, after a reload, whether this service caused it. A reload it did not
  // cause came from the user (F5), another part of the app, or the browser
  // (for example Chrome reloading a tab it discarded to save memory).
  private reportLastReload() {
    try {
      const last = sessionStorage.getItem(LAST_RELOAD_KEY);
      sessionStorage.removeItem(LAST_RELOAD_KEY);
      if (last) {
        console.info(`[update] This page was reloaded by the update service: ${last}.`);
        return;
      }
      const navigation = performance.getEntriesByType?.('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (navigation?.type === 'reload') {
        console.info('[update] This page was reloaded, but not by the update service.');
      }
    } catch {
      // Storage or timing API unavailable: nothing to report.
    }
  }

  private markUpdateApplied() {
    try {
      sessionStorage.setItem(UPDATED_NOTICE_KEY, '1');
    } catch {
      // Storage blocked: the update still applies, unannounced.
    }
  }

  private takeUpdateApplied(): boolean {
    try {
      const applied = sessionStorage.getItem(UPDATED_NOTICE_KEY) !== null;
      sessionStorage.removeItem(UPDATED_NOTICE_KEY);
      return applied;
    } catch {
      return false;
    }
  }

  private autoReloadAllowed(): boolean {
    try {
      const at = Number(sessionStorage.getItem(AUTO_RELOAD_KEY));
      return !at || Date.now() - at >= AUTO_RELOAD_COOLDOWN_MS;
    } catch {
      return true;
    }
  }

  private markAutoReload() {
    try {
      sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()));
    } catch {
      // Storage blocked: the loop guard is off, the reload still happens.
    }
  }

  private confirmDiscard(): Promise<boolean> {
    return new Promise((resolve) =>
      this.notificationService.openDialog({
        title: 'Unsaved Changes',
        message:
          'You have unsaved changes. Updating now will discard them. Save first, then update.',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Update Without Saving',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      }),
    );
  }

  private confirmInterrupt(): Promise<boolean> {
    return new Promise((resolve) =>
      this.notificationService.openDialog({
        title: 'Still Saving',
        message:
          'A save or upload is still in progress. Updating now may interrupt it. Wait for it to finish, then update.',
        cancelButtonText: 'Wait',
        confirmButtonText: 'Update Anyway',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      }),
    );
  }

  // The token is stored before the sign-in finishes (it still has to load the
  // user and clear the previous user's data); a reload in between would skip
  // that.
  private isSigningIn(): boolean {
    try {
      return this.isOnSignInPage() && !!localStorage.getItem('token');
    } catch {
      return false;
    }
  }

  // The sign-in form only; the password reset pages under /auth hold typed
  // text worth keeping.
  private isOnSignInPage(): boolean {
    return /^\/auth(\/login)?\/?$/.test(window.location.pathname);
  }

  // /pdf-preview is also opened by the server-side PDF renderer.
  private isRenderOnlyPage(): boolean {
    return window.location.pathname.startsWith('/pdf-preview');
  }

  // Ticks outside Angular so waiting triggers no change detection; only an
  // actual change to what is shown re-enters the zone.
  private startTicker() {
    if (this.ticker) return;
    this.zone.runOutsideAngular(() => {
      this.ticker = setInterval(() => this.tick(), 1000);
    });
  }

  private stopTicker() {
    if (!this.ticker) return;
    clearInterval(this.ticker);
    this.ticker = null;
  }

  private track(action: UpdateAction) {
    trackEvent('app_update', { update_action: action });
  }

  // A plain reload is enough: the service worker hands a new page load the
  // newest version, keeps the files older tabs still use, and the URL keeps
  // its query params. Only when the running version is broken are the caches
  // dropped too, so the next load cannot come from a damaged copy.
  private async reloadPage() {
    if (this.mustReload()) await this.clearCaches();
    window.location.reload();
  }

  private loadPage(url: string, replace = false) {
    if (replace) {
      window.location.replace(url);
    } else {
      window.location.assign(url);
    }
  }

  private async clearCaches() {
    if (!('caches' in window)) return;
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (error) {
      console.error('Error clearing caches:', error);
    }
  }

  private async checkForUpdate() {
    if (!this.swUpdate.isEnabled) return;

    try {
      await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.error('Error checking for SW update:', error);
    }
  }

  public destroy() {
    if (this.scheduledCheckTimeout) {
      clearTimeout(this.scheduledCheckTimeout);
      this.scheduledCheckTimeout = undefined;
    }
    if (this.periodicCheck) {
      clearInterval(this.periodicCheck);
      this.periodicCheck = null;
    }
    this.stopTicker();

    if (this.beforeInstallHandler) {
      window.removeEventListener('beforeinstallprompt', this.beforeInstallHandler as any);
      this.beforeInstallHandler = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler as any);
      this.visibilityHandler = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler as any);
      this.onlineHandler = null;
    }

    this.subscriptions.unsubscribe();
  }
}
